import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ComplianceStats {
  overdueCount: number;
  dueSoonCount: number;
  pendingApprovalCount: number;
  needAcknowledgmentPolicies: number;
  needAcknowledgmentTotal: number;
  myPoliciesCount: number;
  draftsInProgressCount: number;
}

export function useComplianceStats(userId: string, userRole: string) {
  const { profile } = useAuth();
  const clinicId = profile?.clinic_id;

  return useQuery<ComplianceStats>({
    queryKey: ['compliance-stats', clinicId, userId],
    enabled: !!clinicId,
    queryFn: async (): Promise<ComplianceStats> => {
      try {
      const today = new Date().toISOString().split('T')[0];
      const thirtyDays = new Date();
      thirtyDays.setDate(thirtyDays.getDate() + 30);
      const thirtyDaysStr = thirtyDays.toISOString().split('T')[0];

      // === BATCH 1: All independent queries in parallel ===
      const isAdmin = userRole === 'admin' || userRole === 'owner';

      const batch1Promises = [
        // 0: Overdue
        supabase
          .from('policies')
          .select('id')
          .eq('clinic_id', clinicId)
          .eq('status', 'Approved')
          .or('is_archived.is.null,is_archived.eq.false')
          .not('next_review_date', 'is', null)
          .lt('next_review_date', today),
        // 1: Due soon
        supabase
          .from('policies')
          .select('id')
          .eq('clinic_id', clinicId)
          .eq('status', 'Approved')
          .or('is_archived.is.null,is_archived.eq.false')
          .not('next_review_date', 'is', null)
          .gte('next_review_date', today)
          .lte('next_review_date', thirtyDaysStr),
        // 2: Pending approval
        supabase
          .from('policies')
          .select('id')
          .eq('clinic_id', clinicId)
          .eq('status', 'pending_approval')
          .or('is_archived.is.null,is_archived.eq.false'),
        // 3: Ack policies
        supabase
          .from('policies')
          .select('id')
          .eq('clinic_id', clinicId)
          .eq('status', 'Approved')
          .eq('requires_acknowledgement', true)
          .or('is_archived.is.null,is_archived.eq.false'),
        // 4: Staff profiles
        supabase
          .from('profiles')
          .select('id')
          .eq('clinic_id', clinicId),
        // 5: Drafts in progress
        supabase
          .from('policy_drafts')
          .select('id')
          .eq('clinic_id', clinicId!)
          .eq('status', 'draft'),
      ] as const;

      // Admin: my owned policies; Staff: my assignments
      const myPoliciesPromise = isAdmin
        ? supabase
            .from('policies')
            .select('id')
            .eq('clinic_id', clinicId!)
            .eq('policy_owner', userId)
            .or('is_archived.is.null,is_archived.eq.false')
        : supabase
            .from('policy_assignments')
            .select('policy_id')
            .eq('user_id', userId)
            .neq('assignment_type', 'exclusion');

      const [overdueRes, dueSoonRes, pendingRes, ackPoliciesRes, staffRes, draftsRes, myPoliciesRes] =
        await Promise.all([...batch1Promises, myPoliciesPromise]);

      const overdueCount = overdueRes.data?.length || 0;
      const dueSoonCount = dueSoonRes.data?.length || 0;
      const pendingApprovalCount = pendingRes.data?.length || 0;
      const totalStaff = staffRes.data?.length ?? 0;
      const draftsInProgressCount = draftsRes.data?.length || 0;

      // === Acknowledgement calculation (batch 2, depends on batch 1) ===
      let needAckPolicies = 0;
      let needAckTotal = 0;

      const ackPolicies = ackPoliciesRes.data;
      if (ackPolicies?.length && totalStaff > 0) {
        const policyIds = ackPolicies.map(p => p.id);

        const { data: versions } = await supabase
          .from('policy_versions')
          .select('id, policy_id, version_number')
          .in('policy_id', policyIds)
          .order('version_number', { ascending: false });

        const latestVersions = new Map<string, string>();
        (versions ?? []).forEach(v => {
          if (!latestVersions.has(v.policy_id)) {
            latestVersions.set(v.policy_id, v.id);
          }
        });

        if (latestVersions.size > 0) {
          const versionIds = Array.from(latestVersions.values());
          const { data: acks } = await supabase
            .from('policy_acknowledgements')
            .select('policy_version_id, acknowledged_by')
            .in('policy_version_id', versionIds);

          const acksPerVersion = new Map<string, Set<string>>();
          (acks ?? []).forEach(a => {
            if (!acksPerVersion.has(a.policy_version_id)) {
              acksPerVersion.set(a.policy_version_id, new Set());
            }
            acksPerVersion.get(a.policy_version_id)!.add(a.acknowledged_by);
          });

          latestVersions.forEach((versionId) => {
            const ackedCount = acksPerVersion.get(versionId)?.size ?? 0;
            const missing = totalStaff - ackedCount;
            if (missing > 0) {
              needAckPolicies += 1;
              needAckTotal += missing;
            }
          });
        }
      }

      // === My policies count ===
      let myCount = 0;
      if (isAdmin) {
        myCount = myPoliciesRes.data?.length || 0;
      } else {
        const policyIds = (myPoliciesRes.data || []).map((p: any) => p.policy_id);
        if (policyIds.length > 0) {
          const [pendingRes2, myAcksRes] = await Promise.all([
            supabase
              .from('policies')
              .select('id')
              .eq('clinic_id', clinicId!)
              .in('status', ['Approved', 'Locked'])
              .or('is_archived.is.null,is_archived.eq.false')
              .in('id', policyIds as string[]),
            supabase
              .from('policy_acknowledgements')
              .select('policy_version_id')
              .eq('acknowledged_by', userId),
          ]);

          const ackedPolicyIds = new Set((myAcksRes.data || []).map((a) => a.policy_version_id));
          myCount = (pendingRes2.data || []).filter((p: any) => !ackedPolicyIds.has(p.id)).length;
        }
      }

      return {
        overdueCount,
        dueSoonCount,
        pendingApprovalCount,
        needAcknowledgmentPolicies: needAckPolicies,
        needAcknowledgmentTotal: needAckTotal,
        myPoliciesCount: myCount,
        draftsInProgressCount,
      };
      } catch (error) {
        console.error('Error fetching compliance stats:', error);
        return {
          overdueCount: 0,
          dueSoonCount: 0,
          pendingApprovalCount: 0,
          needAcknowledgmentPolicies: 0,
          needAcknowledgmentTotal: 0,
          myPoliciesCount: 0,
          draftsInProgressCount: 0,
        };
      }
    },
  });
}
